import { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { formatDate } from '../utils/formatters';
import { getCsrfToken } from '../utils/csrfUtils';
/**
 * 維修單相關的鉤子函數
 * 
 * @param {string} token - 認證令牌
 * @returns {Object} - 包含維修單狀態和操作方法的對象
 */
function useRepair(token) {
  // 狀態
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Pending');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // 獲取所有維修單
  const fetchRepairs = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/customers/repairs`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('維修單數據:', response);
      
      // 檢查內容類型以確認是JSON還是HTML
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('API返回非JSON響應:', text.substring(0, 200));
        throw new Error('API返回了無效的響應格式，請檢查API服務是否正常');
      }
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || '獲取維修單時出錯');
      }

      setRepairs(data.repairs || []);
    } catch (err) {
      setError(err.message || '獲取維修單時出錯');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 根據保養單ID獲取維修單
  const fetchRepairsByWarrantyId = useCallback(async (warrantyId) => {
    if (!token || !warrantyId) return [];

    setLoading(true);
    setError(null);
    const idOnly = warrantyId.split('/').pop();
    try {
      const response = await fetch(`/api/customers/warranties/${idOnly}/repairs`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // 檢查內容類型以確認是JSON還是HTML
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('API返回非JSON響應:', text.substring(0, 200));
        throw new Error('API返回了無效的響應格式，請檢查API服務是否正常');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '獲取維修單時出錯');
      }

      return data.repairs || [];
    } catch (err) {
      setError(err.message || '獲取維修單時出錯');
      return [];
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 獲取單個維修單詳情
  const fetchRepairById = useCallback(async (repairId) => {
    if (!token || !repairId) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/customers/repair?id=${repairId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // 檢查內容類型以確認是JSON還是HTML
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // 如果不是JSON，則可能是HTML錯誤頁面
        const text = await response.text();
        console.error('API返回非JSON響應:', text.substring(0, 200));
        throw new Error('API返回了無效的響應格式，請檢查API服務是否正常');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '獲取維修單詳情時出錯');
      }

      // 檢查是否有正確的維修單數據結構
      if (!data || (!data.repair && !data.id)) {
        console.error('API返回的數據格式不正確:', data);
        throw new Error('獲取維修單詳情時出錯: 數據格式不正確');
      }

      // 容錯處理：如果API返回結構不同，嘗試從不同位置獲取維修單數據
      return data.repair || data || null;
    } catch (err) {
      // 記錄詳細錯誤信息
      console.error('獲取維修單詳情時出錯:', err);
      setError(err.message || '獲取維修單詳情時出錯');
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 創建維修單
  const createRepair = useCallback(async (warrantyId, repairData) => {
    if (!token || !warrantyId) return null;

    setUpdateLoading(true);
    setUpdateError(null);
    const idOnly = warrantyId.split('/').pop();
    const csrfToken = getCsrfToken();
    try {
      const response = await fetch(`/api/customers/warranties/${idOnly}/repairs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(repairData)
      });
      
      // 檢查內容類型以確認是JSON還是HTML
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('API返回非JSON響應:', text.substring(0, 200));
        throw new Error('API返回了無效的響應格式，請檢查API服務是否正常');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '創建維修單時出錯');
      }

      // 刷新維修單列表
      fetchRepairs();

      return data.repair || null;
    } catch (err) {
      setUpdateError(err.message || '創建維修單時出錯');
      return null;
    } finally {
      setUpdateLoading(false);
    }
  }, [token, fetchRepairs]);

  // 更新維修單狀態
  const updateRepairStatus = useCallback(async (repairId, status, completionDate) => {
    if (!token || !repairId) return null;

    setUpdateLoading(true);
    setUpdateError(null);
    const csrfToken = getCsrfToken();
    try {
      // 處理ID格式
      let processedId = repairId;
      
      // 如果ID是完整的Shopify格式，提取數字部分
      if (typeof repairId === 'string' && repairId.startsWith('gid://shopify/Metaobject/')) {
        processedId = repairId.split('/').pop();
        console.log('從Shopify ID提取數字部分:', processedId);
      }

      const response = await fetch(`/api/customers/repairs/${processedId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ status, completionDate })
      });
      
      // 檢查內容類型以確認是JSON還是HTML
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('API返回非JSON響應:', text.substring(0, 200));
        throw new Error('API返回了無效的響應格式，請檢查API服務是否正常');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '更新維修單狀態時出錯');
      }

      // 刷新維修單列表
      fetchRepairs();

      return data.repair || null;
    } catch (err) {
      setUpdateError(err.message || '更新維修單狀態時出錯');
      return null;
    } finally {
      setUpdateLoading(false);
    }
  }, [token, fetchRepairs]);

  // 更新維修單詳情
  const updateRepairDetails = useCallback(async (repairId, updateData) => {
    if (!token || !repairId) return null;

    setUpdateLoading(true);
    setUpdateError(null);
    const csrfToken = getCsrfToken();
    try {
      // 處理ID格式
      let processedId = repairId;
      
      // 如果ID是完整的Shopify格式，提取數字部分
      if (typeof repairId === 'string' && repairId.startsWith('gid://shopify/Metaobject/')) {
        processedId = repairId.split('/').pop();
      }
      
      // 準備更新的數據
      const updatePayload = {};
      
      if (updateData.repairDetails !== undefined) {
        updatePayload.repairDetails = updateData.repairDetails;
      }
      
      if (updateData.repairCost !== undefined) {
        updatePayload.repairCost = updateData.repairCost;
      }
      
      console.log('發送更新維修單詳情:', updatePayload);

      const response = await fetch(`/api/customers/repairs/${processedId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(updatePayload)
      });
      
      // 檢查內容類型
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('API返回非JSON響應:', text.substring(0, 200));
        throw new Error('API返回了無效的響應格式，請檢查API服務是否正常');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '更新維修單詳情時出錯');
      }

      // 刷新維修單列表
      fetchRepairs();

      return data.repair || null;
    } catch (err) {
      console.error('更新維修單詳情出錯:', err);
      setUpdateError(err.message || '更新維修單詳情時出錯');
      return null;
    } finally {
      setUpdateLoading(false);
    }
  }, [token, fetchRepairs]);

  // 根據搜索詞過濾維修單
  const filteredRepairs = useMemo(() => {
    if (!repairs.length) return [];

    return repairs.filter(repair => {
      // 搜索條件：技術員名稱、初步診斷、維修詳情等
      const searchFields = [
        repair.technician,
        repair.initial_diagnosis,
        repair.repair_details,
        repair.warranty_id
      ].filter(Boolean).map(field => field.toLowerCase());

      // 檢查是否符合搜索條件
      return (
        searchTerm === '' || 
        searchFields.some(field => field.includes(searchTerm.toLowerCase()))
      );
    });
  }, [repairs, searchTerm]);

  // 按狀態分組的維修單
  const groupedRepairs = useMemo(() => {
    return {
      'Pending': filteredRepairs.filter(repair => repair.status === 'Pending'),
      'In Progress': filteredRepairs.filter(repair => repair.status === 'In Progress'),
      'Completed': filteredRepairs.filter(repair => repair.status === 'Completed'),
      'Cancelled': filteredRepairs.filter(repair => repair.status === 'Cancelled')
    };
  }, [filteredRepairs]);

  // 初始化時加載數據
  useEffect(() => {
    if (token) {
      fetchRepairs();
    }
  }, [token, fetchRepairs]);

  return {
    repairs,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    filteredRepairs,
    groupedRepairs,
    fetchRepairs,
    fetchRepairsByWarrantyId,
    fetchRepairById,
    createRepair,
    updateRepairStatus,
    updateRepairDetails,
    updateLoading,
    updateError,
    formatDate
  };
}

export default useRepair; 